import React, { useState } from 'react';
import _ from 'lodash';
import { Flex, Input } from '@duxton-web/ui';
import { SearchUniversal } from '@duxton-web/icons';
import { colorBaseNeutral75, colorContentSubtle } from '@duxton-web/design-tokens/themes/light';
import { useSelector } from 'react-redux';

import MultiSelectDropdown from 'omega-rtc/components/form/ucd/common/MultiSelectDropdown';
import CountrySelector from 'omega-rtc/components/custom/entities/CountrySelector';
import SingleSelect from 'omega-rtc/components/form/ucd/common/SingleSelect';
import ToggleButton from 'omega-rtc/components/form/ucd/common/ToggleButton';
import MoreFiltersButton from 'omega-rtc/components/form/ucd/MoreFiltersButton';
import { STATUS, FILTER_FIELDS, VERTICALS_DISPLAY_NAMES, INTERNAL_STATUS } from 'omega-rtc/constants/ucd';
import ProjectMoreFilters from 'omega-rtc/components/form/ucd/ProjectMoreFilters';
import CampaignMoreFilters from 'omega-rtc/components/form/ucd/CampaignMoreFilters';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';

interface FilterOption {
  label: string;
  value: string;
}

interface CountryOption {
  label: string;
  value: string;
}

interface FilterCondition {
  lhs: string;
  operator: string;
  rhs: any;
}

interface Props {
  type: 'projects' | 'campaigns';
  router: any;
  filterFields: string[]; // list of fields to show in the filter bar (except text input and more filters)
  countries: CountryOption[];
  moreFiltersCallback: (filters: FilterCondition[]) => void;
  countryChangeCallback: (countries: string[]) => void;
  ownershipChangeCallback: (value: boolean) => void;
  pendingApprovalChangeCallback?: (value: boolean) => void;
  statusChangeCallback: (status: string) => void;
  verticalChangeCallback: (verticals: string[]) => void;
  countryInitialValue: string[];
  ownershipInitialValue: boolean;
  pendingApprovalInitialValue?: boolean;
  statusInitialValue: string;
  verticalsInitialValue: string[];
  moreFiltersInitialValue: FilterCondition[];
  searchTextInitialValue: string;
  searchTextChangeCallback: (searchText: string) => void;
}

const statusOptions: FilterOption[] = Object.values(STATUS).map((s) => ({
  label: _.capitalize(s),
  value: s,
}));

const projectStatusOptions: FilterOption[] = Object.values(INTERNAL_STATUS).map((s) => ({
  label: _.capitalize(s),
  value: s,
}));

const Filter: React.FC<Props> = (props) => {
  const configs = useSelector((state: any) => state.ucd.configs);

  const verticals = configs ? configs.verticals : [];

  const verticalOptions = verticals.map((vertical) => ({
    value: vertical,
    label: _.get(VERTICALS_DISPLAY_NAMES, vertical),
  }));

  const [searchText, setSearchText] = useState(
    props.searchTextInitialValue ? props.searchTextInitialValue : '',
  );

  return (
    <Flex wrap="wrap" align="center">
      <div style={{ marginRight: '15px', marginTop: '10px' }}>
        <Input
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              const searchTextValue = event.currentTarget.value;
              if (searchTextValue) {
                const searchTextSpaceRemoved = searchTextValue.split(' ').join('');
                if (searchTextSpaceRemoved.length < 4) {
                  snackBar.error({ content: 'Enter at least 4 characters to search' });
                  return;
                }
              }
              props.searchTextChangeCallback(searchTextValue);
            }
          }}
          placeholder="Search"
          prefix={<SearchUniversal style={{ fontSize: '16px' }} />}
          style={{
            borderColor: colorBaseNeutral75,
            width: '244px',
            height: '36px',
          }}
        />
      </div>
      {_.includes(props.filterFields, FILTER_FIELDS.myCampaign) && (
        <div style={{ marginRight: '15px', marginTop: '10px' }}>
          <ToggleButton
            initialState={props.ownershipInitialValue}
            width={'125px'}
            height={'36px'}
            callback={props.ownershipChangeCallback}
            title={props.type === 'projects' ? 'My Projects' : 'My Campaigns'}
          />
        </div>
      )}
      {_.includes(props.filterFields, FILTER_FIELDS.pendingApproval) && props.type === 'campaigns' && (
        <div style={{ marginRight: '15px', marginTop: '10px' }}>
          <ToggleButton
            initialState={props.pendingApprovalInitialValue || false}
            width={'185px'}
            height={'36px'}
            callback={props.pendingApprovalChangeCallback || (() => {})}
            title="Pending My Approval"
          />
        </div>
      )}
      {_.includes(props.filterFields, FILTER_FIELDS.country) && (
        <div style={{ marginRight: '15px', marginTop: '10px' }}>
          <CountrySelector initialValue={props.countryInitialValue} onChange={props.countryChangeCallback} multiple />
        </div>
      )}
      {_.includes(props.filterFields, FILTER_FIELDS.vertical) && (
        <div style={{ marginRight: '15px', marginTop: '10px' }}>
          <MultiSelectDropdown
            placeholder="Vertical"
            initialValue={props.verticalsInitialValue}
            options={verticalOptions}
            width={'142px'}
            height={'36px'}
            onChange={props.verticalChangeCallback}
            placeholderStyle={{
              color: colorContentSubtle,
            }}
          />
        </div>
      )}
      {_.includes(props.filterFields, FILTER_FIELDS.status) && (
        <div style={{ marginRight: '15px', marginTop: '10px' }}>
          <SingleSelect
            addNone
            placeholder="Status"
            initialValue={
              props.statusInitialValue ? props.statusInitialValue : undefined
            }
            options={statusOptions}
            width={'125px'}
            height={'36px'}
            onChange={props.statusChangeCallback}
          />
        </div>
      )}
      {_.includes(props.filterFields, FILTER_FIELDS.projectStatus) && (
        <div style={{ marginRight: '15px', marginTop: '10px' }}>
          <SingleSelect
            addNone
            placeholder="Status"
            initialValue={
              props.statusInitialValue ? props.statusInitialValue : undefined
            }
            options={projectStatusOptions}
            width={'125px'}
            height={'36px'}
            onChange={props.statusChangeCallback}
          />
        </div>
      )}
      <div style={{ marginRight: '15px', marginTop: '10px' }}>
        <MoreFiltersButton
          moreFiltersInitialValue={props.moreFiltersInitialValue}
          moreFiltersCallback={props.moreFiltersCallback}
          height={'36px'}
          filterModal={
            props.type === 'campaigns'
              ? CampaignMoreFilters
              : ProjectMoreFilters
          }
        />
      </div>
    </Flex>
  );
};

export default Filter;

